import moment from "moment";
import {
  isBusinessDay,
  nextBusinessDay,
  businessAdd,
} from "@/helpers/momentBusinessDays";

import { CAMPAIGN_CONST, DATE_CONST } from "@/config/constants";
import { CREATIVE_UPDATE_STATUS } from "./extendConstants";
import { getCampaignLaunchBufferDays } from "@/helpers/utils";

type DateValue = string | Date | moment.Moment | null | undefined;

type ExtendEnrollment = {
  start_date?: DateValue;
  end_date?: DateValue;
};

type ExtendCampaignLocation = {
  location_name?: string;
  end_date?: DateValue;
  extend_end_date?: DateValue;
  subscription?: {
    subscription_end_date?: DateValue;
  } | null;
  extend_enrollments?: ExtendEnrollment[] | null;
};

type ExtendCampaignData = {
  evergreen_participant_min_value?: number | string | null;
  evergreen_participant_max_value?: number | string | null;
  availability_window?: string | null;
  availability_window_date?: {
    window_start_date?: DateValue;
    window_end_date?: DateValue;
  } | null;
  location_fees_covered?: boolean | null;
  contract_end_date?: DateValue;
};

type ExtendSelection = {
  extend_start_date?: DateValue;
  extend_end_date?: DateValue;
  extend_budget?: number | string | null;
  can_update_creatives?: boolean;
};

type EligibilityDays = {
  canNotUpdateDate: string[];
  canUpdateDate: string[];
};

type DateRangeConflict = {
  locationName: string;
  conflictingRanges: Array<{
    start_date?: DateValue;
    end_date?: DateValue;
  }>;
};

export const extendCampaignHelper = {
  getExtendCampaignMinMaxStartDate,
  getExtendCampaignMinMaxEndDate,
  getCreativeUpdateStatus,
  findCanUpdateCreative,
  getCreativeUpdateEligibilityDays,
  getExtendCampaignLocStartDate,
  shouldDisableExtendedDate,
  isDateInExtendedRanges,
  validateDateRangeAgainstExtendedRanges,
  getCreativeUpdateEligibilityDaysWithExtensions,
};

function getExtendCampaignMinMaxStartDate(
  loc: ExtendCampaignLocation,
  campaign: ExtendCampaignData
) {
  const {
    // evergreen_participant_min_value,
    availability_window,
    availability_window_date,
    // allow_multiple_enrollments_per_window,
    location_fees_covered,
    contract_end_date,
  } = campaign;

  const { subscription, end_date, extend_end_date } = loc;
  const { subscription_end_date } = subscription || {};

  // Use extend_end_date if it exists, otherwise use end_date
  const effectiveEndDate = extend_end_date ? extend_end_date : end_date;

  const minDurationToExtend = Number(CAMPAIGN_CONST.EXTEND.MIN_EXTEND_DAYS) - 1;

  const isAvailableWindow =
    availability_window &&
    availability_window !==
      CAMPAIGN_CONST.AVAILABILITY_WINDOW.TYPE.ALWAYS_AVAILABLE;

  // launch buffer day calculation
  const today = moment();
  const bufferDays = getCampaignLaunchBufferDays();
  const businessStart = isBusinessDay(today)
    ? today
    : nextBusinessDay(today.clone());

  const withBufferDays = businessAdd(businessStart, bufferDays);

  const nextDayAfterEndDate = moment(effectiveEndDate).add(1, "day");

  const baseMinStart = withBufferDays.isAfter(nextDayAfterEndDate, "day")
    ? withBufferDays
    : nextDayAfterEndDate;

  const minStartDate = moment.max(
    baseMinStart,
    // !location_fees_covered ? moment(subscription_start_date) : baseMinStart, // already subscribed and in subscription is active
    isAvailableWindow
      ? moment(availability_window_date?.window_start_date)
      : baseMinStart
  );

  const latestEnd = moment.min(
    !location_fees_covered
      ? moment(subscription_end_date)
      : moment(contract_end_date),
    isAvailableWindow
      ? moment(availability_window_date?.window_end_date) // OR last_day_to_join
      : moment(DATE_CONST.INFINITY_DATE)
  );

  const maxStartDate = latestEnd.clone().subtract(minDurationToExtend, "days");

  //
  //
  //

  return {
    minStartDate,
    maxStartDate,
  };
}

export function getExtendCampaignMinMaxEndDate(
  startDate: DateValue,
  loc: ExtendCampaignLocation,
  campaign: ExtendCampaignData
) {
  const {
    // evergreen_participant_min_value,
    evergreen_participant_max_value,
    availability_window,
    availability_window_date,
    // allow_multiple_enrollments_per_window,
    location_fees_covered,
    contract_end_date,
  } = campaign;

  const { subscription } = loc;
  const { subscription_end_date } = subscription || {};

  const minDuration = Number(CAMPAIGN_CONST.EXTEND.MIN_EXTEND_DAYS) - 1;

  const maxDuration =
    Number(evergreen_participant_max_value) - 1 || minDuration;

  const isAvailableWindow =
    availability_window &&
    availability_window !==
      CAMPAIGN_CONST.AVAILABILITY_WINDOW.TYPE.ALWAYS_AVAILABLE;

  const minEndDate = moment(startDate).clone().add(minDuration, "days");

  const latestEnd = moment.min(
    !location_fees_covered
      ? moment(subscription_end_date)
      : moment(contract_end_date),
    isAvailableWindow
      ? moment(availability_window_date?.window_end_date)
      : moment(DATE_CONST.INFINITY_DATE)
  );

  const maxEndDate = moment.min(
    moment(startDate).clone().add(maxDuration, "days"),
    latestEnd
  );

  return {
    minEndDate,
    maxEndDate,
  };
}

function getCreativeUpdateStatus(selections: ExtendSelection[]) {
  const filledSelections = selections.filter((selection) => {
    return (
      selection.extend_start_date &&
      selection.extend_end_date &&
      selection.extend_budget
    );
  });

  const canAllUpdateCreatives = filledSelections.every((selection) => {
    return selection.can_update_creatives;
  });

  const canAllNotUpdateCreatives = filledSelections.every((selection) => {
    return !selection.can_update_creatives;
  });

  const canSomeUpdateCreatives = filledSelections.some((selection) => {
    return selection.can_update_creatives;
  });

  const canSomeNotUpdateCreatives = filledSelections.some((selection) => {
    return !selection.can_update_creatives;
  });

  let status = "";
  if (canAllUpdateCreatives) {
    status = CREATIVE_UPDATE_STATUS.ALL_CAN_UPDATE;
  } else if (canAllNotUpdateCreatives) {
    status = CREATIVE_UPDATE_STATUS.ALL_NOT_CAN_UPDATE;
  } else if (canSomeUpdateCreatives) {
    status = CREATIVE_UPDATE_STATUS.MIX_CAN_UPDATE;
  } else if (canSomeNotUpdateCreatives) {
    status = CREATIVE_UPDATE_STATUS.MIX_NOT_CAN_UPDATE;
  }

  return status;
}

function findCanUpdateCreative(
  startDate: DateValue,
  endDate: DateValue,
  campaign: ExtendCampaignData
) {
  const { evergreen_participant_min_value } = campaign;

  const baseMinEndDate = moment(startDate)
    .clone()
    .add(CAMPAIGN_CONST.EXTEND.MIN_EXTEND_DAYS - 1, "days");

  const dateNotEligibleToUpdateCreative = moment(startDate).add(
    Number(evergreen_participant_min_value) - 1,
    "days"
  );

  const isBetween = moment(endDate).isBetween(
    baseMinEndDate,
    dateNotEligibleToUpdateCreative,
    "days",
    "[]"
  );

  return isBetween ? false : true;
}

function getCreativeUpdateEligibilityDays(
  startDate: DateValue,
  loc: ExtendCampaignLocation,
  campaign: ExtendCampaignData
): EligibilityDays {
  const { evergreen_participant_min_value } = campaign;
  const { minEndDate, maxEndDate } = getExtendCampaignMinMaxEndDate(
    startDate,
    loc,
    campaign
  );

  const minJoinDuration = Number(evergreen_participant_min_value) - 1;
  const minJoinDurationDate = moment(startDate).add(minJoinDuration, "days");

  const isMinJoinDurationDateBeforeMinEndDate = minJoinDurationDate.isBefore(
    minEndDate,
    "day"
  );

  const canNotUpdateDateMin = minEndDate;
  const canNotUpdateDateMax = minEndDate.isSameOrAfter(
    minJoinDurationDate,
    "day"
  )
    ? minEndDate
    : minJoinDurationDate;

  let canNotUpdateDate = [
    canNotUpdateDateMin.format("YYYY-MM-DD"),
    canNotUpdateDateMax.format("YYYY-MM-DD"),
  ];

  if (isMinJoinDurationDateBeforeMinEndDate) {
    canNotUpdateDate = [];
  }

  const canUpdateDateMin = isMinJoinDurationDateBeforeMinEndDate
    ? minEndDate
    : canNotUpdateDateMax.clone().add(1, "days");
  const canUpdateDateMax = maxEndDate;
  const canUpdateDate = [
    canUpdateDateMin.format("YYYY-MM-DD"),
    canUpdateDateMax.format("YYYY-MM-DD"),
  ];

  return {
    canNotUpdateDate,
    canUpdateDate,
  };
}

function getExtendCampaignLocStartDate(
  loc: ExtendCampaignLocation,
  campaign: ExtendCampaignData
) {
  const { end_date, extend_end_date } = loc;
  // Use extend_end_date if it exists, otherwise use end_date
  const effectiveEndDate = extend_end_date ? extend_end_date : end_date;
  const { minStartDate } = getExtendCampaignMinMaxStartDate(loc, campaign);

  const locJoinEndDate = moment(effectiveEndDate);

  return locJoinEndDate.isValid() &&
    moment(minStartDate).isValid() &&
    locJoinEndDate.clone().add(1, "day").isSame(minStartDate, "day")
    ? minStartDate
    : "";
}

/**
 * Checks if a date should be disabled because it falls within an extended enrollment range
 * @param {moment.Moment} date - The date to check
 * @param {Object} loc - Location object with extend_enrollments array
 * @returns {boolean} true if date should be disabled (falls within extended range), false otherwise
 */
function shouldDisableExtendedDate(
  date: moment.Moment | null | undefined,
  loc?: ExtendCampaignLocation | null
) {
  if (!date || !date.isValid()) return false;

  const { extend_enrollments } = loc || {};

  // If no extend_enrollments, nothing to disable
  if (
    !extend_enrollments ||
    !Array.isArray(extend_enrollments) ||
    extend_enrollments.length === 0
  ) {
    return false;
  }

  // Check if date falls within any extended enrollment range
  return extend_enrollments.some((enrollment) => {
    const rangeStart = moment(enrollment.start_date);
    const rangeEnd = moment(enrollment.end_date);
    return (
      date.isSameOrAfter(rangeStart, "day") &&
      date.isSameOrBefore(rangeEnd, "day")
    );
  });
}

/**
 * Checks if a date falls within any extended enrollment ranges
 * @param {moment.Moment|string} date - The date to check
 * @param {Object} loc - Location object with extend_enrollments array
 * @returns {Object|null} Returns the conflicting enrollment range or null
 */
function isDateInExtendedRanges(
  date: DateValue,
  loc?: ExtendCampaignLocation | null
): ExtendEnrollment | null {
  if (!date) return null;

  const dateMoment = moment.isMoment(date) ? date : moment(date);
  if (!dateMoment.isValid()) return null;

  const { extend_enrollments } = loc || {};

  if (
    !extend_enrollments ||
    !Array.isArray(extend_enrollments) ||
    extend_enrollments.length === 0
  ) {
    return null;
  }

  // Find the enrollment range that contains this date
  const conflictingRange = extend_enrollments.find((enrollment) => {
    const rangeStart = moment(enrollment.start_date);
    const rangeEnd = moment(enrollment.end_date);
    return (
      dateMoment.isSameOrAfter(rangeStart, "day") &&
      dateMoment.isSameOrBefore(rangeEnd, "day")
    );
  });

  return conflictingRange || null;
}

/**
 * Validates a date range against extended enrollment ranges
 * @param {string} startDate - Start date string (YYYY-MM-DD)
 * @param {string} endDate - End date string (YYYY-MM-DD)
 * @param {Object} loc - Location object with extend_enrollments array
 * @returns {Object|null} Returns conflict info { locationName, conflictingRanges } or null if valid
 */
function validateDateRangeAgainstExtendedRanges(
  startDate: DateValue,
  endDate: DateValue,
  loc?: ExtendCampaignLocation | null
): DateRangeConflict | null {
  if (!startDate || !endDate || !loc) return null;

  const startMoment = moment(startDate);
  const endMoment = moment(endDate);

  if (!startMoment.isValid() || !endMoment.isValid()) return null;

  const { extend_enrollments, location_name } = loc || {};

  if (
    !extend_enrollments ||
    !Array.isArray(extend_enrollments) ||
    extend_enrollments.length === 0
  ) {
    return null;
  }

  // Find all extended ranges that overlap with the selected date range
  const conflictingRanges = extend_enrollments.filter((enrollment) => {
    const rangeStart = moment(enrollment.start_date);
    const rangeEnd = moment(enrollment.end_date);

    // Check if ranges overlap
    return (
      (startMoment.isSameOrAfter(rangeStart, "day") &&
        startMoment.isSameOrBefore(rangeEnd, "day")) ||
      (endMoment.isSameOrAfter(rangeStart, "day") &&
        endMoment.isSameOrBefore(rangeEnd, "day")) ||
      (startMoment.isBefore(rangeStart, "day") &&
        endMoment.isAfter(rangeEnd, "day"))
    );
  });

  if (conflictingRanges.length === 0) return null;

  return {
    locationName: location_name || "Location",
    conflictingRanges: conflictingRanges.map((range) => ({
      start_date: range.start_date,
      end_date: range.end_date,
    })),
  };
}

/**
 * Gets creative update eligibility days with extension overlap handling
 * Handles both single location and bulk selection (multiple locations)
 * Simple rule: If start_date is before/equal to any extension, constrain eligibility to end before that extension starts
 * @param {string|moment.Moment} startDate - Start date
 * @param {Object|Array} locOrLocations - Single location object OR array of location objects
 * @param {Object} campaign - Campaign object
 * @returns {Object} { canNotUpdateDate, canUpdateDate } constrained by extensions
 */
function getCreativeUpdateEligibilityDaysWithExtensions(
  startDate: DateValue,
  locOrLocations: ExtendCampaignLocation | ExtendCampaignLocation[],
  campaign: ExtendCampaignData
): EligibilityDays {
  if (!startDate) {
    return { canNotUpdateDate: [], canUpdateDate: [] };
  }

  const startMoment = moment.isMoment(startDate)
    ? startDate
    : moment(startDate);
  if (!startMoment.isValid()) {
    return { canNotUpdateDate: [], canUpdateDate: [] };
  }

  // Determine if this is bulk selection (array) or single location (object)
  const isBulkSelection = Array.isArray(locOrLocations);
  const locationData = isBulkSelection ? locOrLocations : [locOrLocations];
  const firstLoc = locationData[0];

  if (!firstLoc) {
    return { canNotUpdateDate: [], canUpdateDate: [] };
  }

  // Get original creative eligibility (existing logic unchanged)
  const originalEligibility = getCreativeUpdateEligibilityDays(
    startDate,
    firstLoc,
    campaign
  );

  // Collect all extend_enrollments from all locations (for bulk) or single location
  let allExtendEnrollments: ExtendEnrollment[] = [];
  locationData.forEach((loc) => {
    const { extend_enrollments } = loc || {};
    if (
      extend_enrollments &&
      Array.isArray(extend_enrollments) &&
      extend_enrollments.length > 0
    ) {
      allExtendEnrollments = allExtendEnrollments?.concat(extend_enrollments);
    }
  });

  // If no extensions, return original eligibility
  if (allExtendEnrollments.length === 0) {
    return originalEligibility;
  }

  // Find the earliest extension start date that comes after the selected start date
  // This is the point where we need to cut off creative eligibility
  let earliestExtensionStart: moment.Moment | null = null;

  allExtendEnrollments.forEach((enrollment) => {
    const extStart = moment(enrollment.start_date);
    if (extStart.isValid() && startMoment.isSameOrBefore(extStart, "day")) {
      if (
        !earliestExtensionStart ||
        extStart.isBefore(earliestExtensionStart, "day")
      ) {
        earliestExtensionStart = extStart;
      }
    }
  });

  // If no extension overlaps (start date is after all extensions), return original eligibility
  if (!earliestExtensionStart) {
    return originalEligibility;
  }

  // Constrain eligibility to end before the earliest extension start
  const constraintDate = earliestExtensionStart.clone().subtract(1, "day");

  // Constrain canNotUpdateDate range
  let canNotUpdateDate: string[] = [];
  if (originalEligibility.canNotUpdateDate.length > 0) {
    const canNotUpdateStart = moment(originalEligibility.canNotUpdateDate[0]);
    const canNotUpdateEnd = moment(originalEligibility.canNotUpdateDate[1]);

    // Only include if it's before the constraint date
    if (canNotUpdateStart.isSameOrBefore(constraintDate, "day")) {
      const constrainedEnd = moment.min(canNotUpdateEnd, constraintDate);
      if (canNotUpdateStart.isSameOrBefore(constrainedEnd, "day")) {
        canNotUpdateDate = [
          canNotUpdateStart.format("YYYY-MM-DD"),
          constrainedEnd.format("YYYY-MM-DD"),
        ];
      }
    }
  }

  // Constrain canUpdateDate range
  let canUpdateDate: string[] = [];
  if (originalEligibility.canUpdateDate.length > 0) {
    const canUpdateStart = moment(originalEligibility.canUpdateDate[0]);
    const canUpdateEnd = moment(originalEligibility.canUpdateDate[1]);

    // Only include if it's before the constraint date
    if (canUpdateStart.isSameOrBefore(constraintDate, "day")) {
      const constrainedEnd = moment.min(canUpdateEnd, constraintDate);
      if (canUpdateStart.isSameOrBefore(constrainedEnd, "day")) {
        canUpdateDate = [
          canUpdateStart.format("YYYY-MM-DD"),
          constrainedEnd.format("YYYY-MM-DD"),
        ];
      }
    }
  }

  return {
    canNotUpdateDate,
    canUpdateDate,
  };
}
